import { motion } from 'framer-motion';
import { Check, Users, Award, Heart } from 'lucide-react';

const MASCOT_URL = "https://customer-assets.emergentagent.com/job_b35da085-09af-4a55-863c-9eb3e423a8d1/artifacts/erel0x7n_buzon.png";

const values = [
  {
    icon: Users,
    title: 'Compromiso',
    description: 'Cada paquete es tratado con el mismo cuidado que si fuera nuestro',
  },
  {
    icon: Award,
    title: 'Excelencia',
    description: 'Nos esforzamos por superar las expectativas en cada envío',
  },
  {
    icon: Heart,
    title: 'Cercanía',
    description: 'Entendemos la importancia de conectar familias y seres queridos',
  },
];

const highlights = [
  'Más de 10,000 paquetes enviados',
  'Presencia en 4 países latinoamericanos',
  'Atención personalizada 24/7',
  'Seguimiento en tiempo real',
  'Embalaje profesional incluido',
  'Precios transparentes sin sorpresas',
];

export const AboutUs = () => {
  return (
    <section 
      id="nosotros" 
      className="relative py-24 md:py-32 bg-white overflow-hidden"
      data-testid="about-section"
    >
      {/* Turquoise blob background */}
      <div className="turquoise-blob -right-32 top-1/2 -translate-y-1/2" />
      
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        <div className="grid lg:grid-cols-2 gap-16 items-center">
          {/* Text Content */}
          <motion.div
            initial={{ opacity: 0, x: -30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
          >
            <span className="inline-block bg-[#2DD4BF]/10 text-[#2DD4BF] px-4 py-2 rounded-full text-sm font-semibold mb-4">
              Sobre Nosotros
            </span>
            
            <h2 className="text-3xl sm:text-4xl font-bold text-slate-900 mb-6">
              Conectamos <span className="text-[#0066CC]">familias</span> a través de fronteras
            </h2>
            
            <p className="text-lg text-slate-600 mb-6 leading-relaxed">
              En <strong className="text-[#F5A623]">Tecno Envios Express</strong>, nacimos con un propósito: 
              hacer que enviar paquetes a tus seres queridos en Latinoamérica sea tan fácil como 
              enviar un mensaje. Desde entonces, hemos ayudado a miles de familias a mantenerse 
              conectadas.
            </p>
            
            <p className="text-lg text-slate-600 mb-8 leading-relaxed">
              Con cobertura en <span className="font-semibold">Panamá, Venezuela, Colombia y República Dominicana</span>, 
              somos tu puente de confianza para envíos internacionales seguros y puntuales.
            </p>
            
            {/* Highlights Grid */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              {highlights.map((item, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, x: -20 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.1 }}
                  className="flex items-center gap-3"
                >
                  <div className="w-6 h-6 bg-[#2DD4BF]/20 rounded-full flex items-center justify-center flex-shrink-0">
                    <Check className="w-4 h-4 text-[#2DD4BF]" />
                  </div>
                  <span className="text-slate-700">{item}</span>
                </motion.div>
              ))}
            </div>
          </motion.div>
          
          {/* Mascot and Values */}
          <motion.div
            initial={{ opacity: 0, x: 30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            className="relative"
          >
            {/* Mascot */}
            <div className="relative mb-8 flex justify-center">
              <div className="relative">
                <div className="absolute inset-0 bg-[#2DD4BF]/20 rounded-full blur-3xl scale-75" />
                <motion.img
                  src={MASCOT_URL}
                  alt="Mascota Tecno Envios"
                  className="relative z-10 w-48 h-48 object-contain"
                  animate={{ rotate: [-3, 3, -3] }}
                  transition={{ duration: 4, repeat: Infinity, ease: "easeInOut" }}
                  data-testid="about-mascot"
                />
              </div>
            </div>
            
            {/* Values Cards */}
            <div className="space-y-4">
              {values.map((value, index) => (
                <motion.div
                  key={value.title}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.15 }}
                  className="bg-white rounded-2xl p-6 border border-slate-100 shadow-sm hover:shadow-lg transition-shadow"
                  data-testid={`value-${index}`}
                >
                  <div className="flex items-start gap-4">
                    <div className="w-12 h-12 bg-gradient-to-br from-[#0066CC] to-[#2DD4BF] rounded-xl flex items-center justify-center flex-shrink-0">
                      <value.icon className="w-6 h-6 text-white" />
                    </div>
                    <div>
                      <h3 className="font-bold text-slate-900 mb-1">{value.title}</h3>
                      <p className="text-slate-600 text-sm">{value.description}</p>
                    </div>
                  </div>
                </motion.div>
              ))}
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

export default AboutUs;
